/**
 * The visitor's authorization before chatting (ADR-0089).
 *
 * When the chatbot asks for one, nothing reaches the API until the visitor
 * accepts: no session, no question. What they accepted lives in this
 * browser per site key, with the version of the text. It holds in every
 * language of the chatbot while that version is still one of its current
 * texts (`versions`); a changed text, or the customer asking everyone again,
 * asks again. The RAG records it when a session is earned with it, and
 * refuses a session without one (`consent_required`).
 */

const STORAGE_PREFIX = 'chatbot_consent:';
const DECLINED_PREFIX = 'chatbot_consent_declined:';

function read(storage, key) {
  try {
    return JSON.parse(storage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function write(storage, key, value) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the decision lives for this page only.
  }
}

/** Kept for the page when storage is blocked. */
const memory = new Map();

/**
 * The stored authorization, when it is for this text or for another
 * language's current one (`versions`, from widget-config).
 */
export function current(siteKey, version, storage = globalThis.localStorage, versions = []) {
  const value = (storage && read(storage, STORAGE_PREFIX + siteKey)) || memory.get(siteKey) || null;
  return value && value.id && (value.version === version || versions.includes(value.version)) ? value : null;
}

export function remember(siteKey, consent, storage = globalThis.localStorage) {
  memory.set(siteKey, consent);
  if (storage) write(storage, STORAGE_PREFIX + siteKey, consent);
  return consent;
}

export function forget(siteKey, storage = globalThis.localStorage) {
  memory.delete(siteKey);
  if (storage) write(storage, STORAGE_PREFIX + siteKey, null);
}

/** "No" is remembered for this tab only: a new visit asks again. */
export function declined(siteKey, version, storage = globalThis.sessionStorage) {
  return Boolean(storage) && read(storage, DECLINED_PREFIX + siteKey) === version;
}

export function rememberDeclined(siteKey, version, storage = globalThis.sessionStorage) {
  if (storage) write(storage, DECLINED_PREFIX + siteKey, version);
}

export function newId() {
  if (globalThis.crypto && crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Whether an API answer is "this chatbot wants an authorization you don't
 * have" (403 consent_required). Every route that takes a chat session can
 * answer it; the panel registers what to do (ask again).
 */
let onRequired = null;
export function whenRequired(handler) {
  onRequired = handler;
}
export function isRequired(status, body) {
  const required = status === 403 && Boolean(body) && body.error === 'consent_required';
  if (required && onRequired) onRequired();
  return required;
}

// --- The message ------------------------------------------------------------
// Drupal sends it filtered to <p> <br> <strong> <em> <a href>. It is the
// customer's text, not model output, but it is still drawn on their
// visitors' pages: never handed to the browser's HTML parser. The allowed
// tags are rebuilt as DOM nodes; any other tag is dropped (its text kept,
// except inside script/style), and a link keeps only an http(s) or mailto
// address.

const ALLOWED = new Set(['p', 'br', 'strong', 'em', 'a']);
const DROP_CONTENT = new Set(['script', 'style', 'template']);
const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
const TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>|<!--[\s\S]*?-->|([^<]+)|</g;

function decode(text) {
  return text.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (match, entity) => {
    if (entity[0] === '#') {
      const code = entity[1] === 'x' || entity[1] === 'X' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[entity.toLowerCase()] ?? match;
  });
}

export function safeHref(value) {
  try {
    const url = new URL(decode(value.trim()));
    return ['https:', 'http:', 'mailto:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function hrefOf(attributes) {
  const match = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(attributes);
  return match ? safeHref(match[1] ?? match[2] ?? match[3]) : null;
}

export function renderConsentHtml(parent, html) {
  const doc = parent.ownerDocument;
  const stack = [parent];
  let skipping = null;
  for (const [, closing, rawTag, attributes, text] of String(html || '').matchAll(TOKEN)) {
    const tag = rawTag && rawTag.toLowerCase();
    if (skipping) {
      if (closing && tag === skipping) skipping = null;
      continue;
    }
    const top = stack[stack.length - 1];
    if (text !== undefined) {
      top.appendChild(doc.createTextNode(decode(text)));
    }
    else if (!tag) {
      // A lone "<" or a comment: "<" stays as text, a comment goes.
      continue;
    }
    else if (DROP_CONTENT.has(tag)) {
      if (!closing) skipping = tag;
    }
    else if (!ALLOWED.has(tag)) {
      continue;
    }
    else if (closing) {
      // Close up to the matching open element; a stray closing tag is ignored.
      let at = stack.length - 1;
      while (at > 0 && String(stack[at].tagName).toLowerCase() !== tag) at -= 1;
      if (at > 0) stack.length = at;
    }
    else if (tag === 'br') {
      top.appendChild(doc.createElement('br'));
    }
    else {
      const node = doc.createElement(tag);
      if (tag === 'a') {
        const href = hrefOf(attributes);
        if (href) {
          node.setAttribute('href', href);
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer nofollow');
        }
      }
      top.appendChild(node);
      stack.push(node);
    }
  }
  return parent;
}
